// Localização: frontend/src/components/Stripe/SubscriptionCard.jsx
// Card visual mostrando informações da assinatura ativa

import React from 'react';
import { 
    CreditCard, 
    Calendar, 
    CheckCircle2, 
    XCircle, 
    AlertCircle,
    Edit,
    Trash2
} from 'lucide-react';

const SubscriptionCard = ({ subscription, onCancel, onUpdate }) => {
    if (!subscription) {
        return null;
    }

    // Mapear status para cores e ícones
    const getStatusInfo = (status) => {
        switch (status?.toUpperCase()) {
            case 'ATIVO':
                return {
                    color: 'text-green-600',
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200',
                    icon: CheckCircle2,
                    label: 'Ativa'
                };
            case 'PENDENTE':
                return {
                    color: 'text-yellow-600',
                    bgColor: 'bg-yellow-50',
                    borderColor: 'border-yellow-200',
                    icon: AlertCircle,
                    label: 'Pendente'
                };
            case 'ATRASADO':
                return {
                    color: 'text-red-600',
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200',
                    icon: AlertCircle,
                    label: 'Atrasada'
                };
            case 'CANCELADO':
                return {
                    color: 'text-gray-600',
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200',
                    icon: XCircle,
                    label: 'Cancelada'
                };
            default:
                return {
                    color: 'text-gray-600',
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200',
                    icon: AlertCircle,
                    label: status || 'Desconhecido'
                };
        }
    };

    const statusInfo = getStatusInfo(subscription.status);
    const StatusIcon = statusInfo.icon;

    // Formatar data
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Formatar valor
    const formatCurrency = (value) => {
        if (!value) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    return (
        <div className={`bg-white rounded-lg shadow-md border-2 ${statusInfo.borderColor} overflow-hidden`}>
            {/* Header com Status */}
            <div className={`${statusInfo.bgColor} px-6 py-4 border-b ${statusInfo.borderColor}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <StatusIcon className={`h-6 w-6 ${statusInfo.color}`} />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {subscription.plano_nome || 'Plano'}
                            </h3>
                            <p className={`text-sm font-medium ${statusInfo.color}`}>
                                {statusInfo.label}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                            {formatCurrency(subscription.plano_valor)}
                        </p>
                        <p className="text-xs text-gray-500">
                            {subscription.plano_frequencia === 'yearly' ? 'por ano' : 'por mês'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Conteúdo */}
            <div className="p-6 space-y-4">
                {/* Informações da Assinatura */}
                <div className="space-y-3">
                    {subscription.data_inicio && (
                        <div className="flex items-center gap-3 text-sm">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Início:</span>
                            <span className="font-medium text-gray-900">
                                {formatDate(subscription.data_inicio)}
                            </span>
                        </div>
                    )}

                    {subscription.data_expiracao && (
                        <div className="flex items-center gap-3 text-sm">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Expira em:</span>
                            <span className="font-medium text-gray-900">
                                {formatDate(subscription.data_expiracao)}
                            </span>
                        </div>
                    )}

                    {subscription.proxima_cobranca && (
                        <div className="flex items-center gap-3 text-sm">
                            <CreditCard className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Próxima cobrança:</span>
                            <span className="font-medium text-gray-900">
                                {formatDate(subscription.proxima_cobranca)}
                            </span>
                        </div>
                    )}

                    {subscription.is_trial && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-800">
                                <strong>Período de Trial:</strong> Você está no período de avaliação de 14 dias.
                            </p>
                        </div>
                    )}
                </div>

                {/* Ações */}
                {(subscription.status === 'ATIVO' || subscription.status === 'PENDENTE') && (
                    <div className="pt-4 border-t border-gray-200">
                        {subscription.status === 'PENDENTE' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                <p className="text-sm text-yellow-800">
                                    <strong>Atenção:</strong> Sua assinatura está aguardando confirmação de pagamento.
                                </p>
                            </div>
                        )}
                        
                        <div className="flex gap-3">
                            {subscription.status === 'ATIVO' && onUpdate && (
                                <button
                                    onClick={onUpdate}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                                >
                                    <Edit className="h-4 w-4" />
                                    <span>Alterar Plano</span>
                                </button>
                            )}
                            {onCancel && (
                                <button
                                    onClick={onCancel}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium ${subscription.status === 'ATIVO' && onUpdate ? '' : 'w-full'}`}
                                >
                                    <Trash2 className="h-4 w-4" />
                                    <span>{subscription.status === 'PENDENTE' ? 'Cancelar Assinatura Pendente' : 'Cancelar'}</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {subscription.status === 'ATRASADO' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-800">
                            <strong>Atenção:</strong> O pagamento da sua assinatura falhou. 
                            Por favor, atualize seu método de pagamento.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionCard;

